import React from "react";
import { Container } from "reactstrap";
import { Link, useParams, useLocation, useNavigate, Navigate } from "react-router-dom";
import FooterBlack from "components/Footers/FooterBlack.js";
import { getApp } from "data/apps.js";

const BASE_TABS = [
  { label: "Support",            hash: "#support" },
  { label: "Privacy Policy",     hash: "#privacy" },
  { label: "Terms & Conditions", hash: "#terms"   },
];

function buildTabs(app) {
  const tabs = [...BASE_TABS];
  if (app && app.dataDeletion) {
    tabs.push({ label: "Data Deletion", hash: "#data-deletion" });
  }
  return tabs;
}

function hashToIndex(tabs, hash) {
  const i = tabs.findIndex((t) => t.hash === hash);
  return i >= 0 ? i : 0;
}

function FaqAccordion({ faq }) {
  const [openIndex, setOpenIndex] = React.useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  if (!faq || faq.length === 0) {
    return <p style={{ color: "var(--ep-muted)" }}>No FAQ available yet.</p>;
  }

  return (
    <div className="faq-list">
      {faq.map((item, i) => (
        <div key={i} className={`faq-item${openIndex === i ? " open" : ""}`}>
          <button className="faq-question" onClick={() => toggle(i)}>
            {item.q}
            <svg className="faq-chevron" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <div className="faq-answer">
            <p>{item.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function SupportTab({ app }) {
  return (
    <div>
      <div className="support-contact-box">
        <div>
          <p>
            <strong>Email support</strong> — {app.support.email}
          </p>
          {app.support.responseTime && (
            <p>Response time: {app.support.responseTime}</p>
          )}
        </div>
        <a href={`mailto:${app.support.email}`} className="ep-btn-primary">
          Email Us
        </a>
      </div>

      <span className="ep-section-eyebrow">Frequently Asked Questions</span>
      <FaqAccordion faq={app.support.faq} />
    </div>
  );
}

function PolicyTab({ data, type }) {
  if (!data || !data.sections || data.sections.length === 0) {
    return (
      <p style={{ color: "var(--ep-muted)" }}>
        {type} not yet available. Contact {" "}
        <a href="mailto:support@edgeventures.ai" style={{ color: "var(--ep-orange)" }}>
          support@edgeventures.ai
        </a>
        {" "} for information.
      </p>
    );
  }

  return (
    <div>
      {data.effectiveDate && (
        <span className="policy-meta">
          Effective: {data.effectiveDate}
          {data.lastUpdated ? `  ·  Last updated: ${data.lastUpdated}` : ""}
        </span>
      )}
      <div className="policy-sections">
        {data.sections.map((section, i) => (
          <div key={i} className="policy-section">
            <h3>{section.title}</h3>
            <p>{section.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DataDeletionTab({ data }) {
  const mailto =
    `mailto:${data.email}` +
    `?subject=${encodeURIComponent(data.mailtoSubject)}` +
    `&body=${encodeURIComponent(data.mailtoBody)}`;

  return (
    <div>
      {data.intro && (
        <p style={{ color: "var(--ep-muted)", marginBottom: 28 }}>{data.intro}</p>
      )}

      <div className="policy-sections">
        {data.appSteps && (
          <div className="policy-section">
            <h3>Delete from the app</h3>
            <p>{data.appSteps}</p>
            {data.screenshots && data.screenshots.length > 0 && (
              <div className="data-deletion-shots">
                {data.screenshots.map((shot, i) => (
                  <figure key={i} className="data-deletion-shot">
                    <img src={shot.src} alt={shot.caption} />
                    <figcaption>{shot.caption}</figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="policy-section">
          <h3>Request deletion by email</h3>
          <p>
            Send an email to{" "}
            <a href={`mailto:${data.email}`} style={{ color: "var(--ep-orange)" }}>
              {data.email}
            </a>{" "}
            with the subject line “{data.mailtoSubject}” from the email address associated
            with your account. To help us locate your account quickly, please include the
            email address you used to sign up for Maven and your display name, if you set one.
          </p>
          <a href={mailto} className="ep-btn-primary" style={{ marginTop: 16 }}>
            Email your deletion request
          </a>
        </div>

        {data.whatGetsDeleted && data.whatGetsDeleted.length > 0 && (
          <div className="policy-section">
            <h3>What gets deleted</h3>
            <p>
              {"When your request is processed, we permanently delete:\n" +
                data.whatGetsDeleted.map((item) => `• ${item}`).join("\n")}
            </p>
          </div>
        )}

        {data.timeline && (
          <div className="policy-section">
            <h3>Timeline</h3>
            <p>{data.timeline}</p>
          </div>
        )}

        <div className="policy-section">
          <h3>Questions</h3>
          <p>
            For any questions about deleting your data, contact{" "}
            <a href={`mailto:${data.email}`} style={{ color: "var(--ep-orange)" }}>
              {data.email}
            </a>
            .{"\n"}Edge Ventures, Inc. · Los Angeles, CA
          </p>
        </div>
      </div>
    </div>
  );
}

function ProductPage() {
  const { appSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const app = getApp(appSlug);
  const tabs = buildTabs(app);
  const activeTab = hashToIndex(tabs, location.hash);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [appSlug]);

  if (!app || app.comingSoon) {
    return <Navigate to="/products" replace />;
  }

  return (
    <div className="product-page">
      <div className="edge-page-brand-nav">
        <Container>
          <Link to="/presentation">Edge<span>Ventures</span></Link>
        </Container>
      </div>

      <div className="product-page-header">
        <Container>
          <Link to="/products" className="product-page-back">
            ← All Products
          </Link>

          <div className="product-page-app-row">
            <div className="product-page-app-icon">
              {app.icon ? (
                <img src={app.icon} alt={app.name} style={{ width: 56, height: 56, borderRadius: 14 }} />
              ) : (
                <span>🎨</span>
              )}
            </div>
            <div>
              <h1 className="product-page-app-name">{app.name}</h1>
              <p className="product-page-app-tagline">{app.tagline}</p>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                {app.playStoreUrl && (
                  <a
                    href={app.playStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="store-badge"
                  >
                    ↗ Google Play
                  </a>
                )}
                {app.appStoreUrl && (
                  <a
                    href={app.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="store-badge"
                  >
                    ↗ App Store
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="ep-tabs">
            {tabs.map((tab, i) => (
              <button
                key={tab.hash}
                className={`ep-tab${activeTab === i ? " active" : ""}`}
                onClick={() => navigate(tab.hash, { replace: true })}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </Container>
      </div>

      <div className="ep-tab-content">
        <Container>
          {tabs[activeTab].hash === "#support" && <SupportTab app={app} />}
          {tabs[activeTab].hash === "#privacy" && (
            <PolicyTab data={app.privacy} type="Privacy Policy" />
          )}
          {tabs[activeTab].hash === "#terms" && (
            <PolicyTab data={app.terms} type="Terms & Conditions" />
          )}
          {tabs[activeTab].hash === "#data-deletion" && (
            <DataDeletionTab data={app.dataDeletion} />
          )}
        </Container>
      </div>

      <FooterBlack />
    </div>
  );
}

export default ProductPage;
